import { h, type ComponentChildren } from 'preact'
import { useEffect, useRef } from 'preact/hooks'

interface ModalProps {
  onDismiss: () => void
  children: ComponentChildren
  closeLabel?: string
  /** v0.12: when true, the modal grows toward near-fullscreen on desktop
   *  and full-screen on mobile. Drives the "transport" feel for the Board
   *  tab — opening the tab feels like clicking through to a real page. */
  expanded?: boolean
}

export function Modal({ onDismiss, children, closeLabel = 'Close', expanded = false }: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<Element | null>(null)

  // Esc-to-close + focus management. Without this, users couldn't dismiss
  // with the keyboard (a real-world UX expectation). Listener installed on
  // window so it works regardless of which element inside the modal has focus.
  useEffect(() => {
    previouslyFocused.current = document.activeElement
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Ignore Esc when a topmost dialog (e.g. the Annotator) is mounted in
      // our shadow root — it owns the keystroke. Without this guard the
      // Esc fires both handlers on the same event and dismisses the form
      // behind the annotator.
      const root = modalRef.current?.getRootNode()
      if (
        root instanceof ShadowRoot &&
        root.querySelector('.annotator-backdrop')
      ) {
        return
      }
      e.stopPropagation()
      onDismiss()
    }
    window.addEventListener('keydown', onKey)
    // Focus first focusable element inside the modal so the keyboard user
    // lands inside, not still on the FAB or whatever opened the modal.
    const first = modalRef.current?.querySelector<HTMLElement>(
      'textarea, input, select, button',
    )
    first?.focus()
    return () => {
      window.removeEventListener('keydown', onKey)
      // Restore focus on dismiss so screen-readers don't lose place.
      const prev = previouslyFocused.current as HTMLElement | null
      if (prev && typeof prev.focus === 'function') prev.focus()
    }
  }, [onDismiss])

  return (
    <div
      class={`backdrop ${expanded ? 'is-expanded' : ''}`}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onDismiss()
      }}
    >
      <div
        ref={modalRef}
        class={`modal ${expanded ? 'is-expanded' : ''}`}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          class="modal-close"
          aria-label={closeLabel}
          onClick={onDismiss}
        >
          ×
        </button>
        {children}
      </div>
    </div>
  )
}
